/**
 * PasswordStrengthIndicator Component
 *
 * Visually indicates the strength of a given password and provides a checklist
 * of key password requirements. Highlights password rules as the user types.
 *
 * @param {Object} props - Component props
 * @param {string} props.password - The password string to evaluate and display strength for
 *
 * Features:
 * - Displays three horizontal bars representing different strength levels.
 * - Highlights bars incrementally based on password strength.
 * - Checks for key password criteria:
 *    1. At least one uppercase letter.
 *    2. Minimum length of 8 characters.
 *    3. At least one numeric digit.
 * - Dynamically changes text color of requirements when satisfied.
 * - Fully localized using react-i18next.
 *
 * Usage:
 * <PasswordStrengthIndicator password={passwordValue} />
 *
 * Notes:
 * - Uses the `testPasswordStrength` utility to calculate overall strength (weak, medium, strong).
 * - Colors are applied to bars and checklist items to communicate progress visually.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { PasswordStrengthIndicatorProps } from "../../types/GeneralComponents";
import { testPasswordStrength } from "../../utils/Functions";

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
}) => {
  const { t } = useTranslation();

  const strength = testPasswordStrength(password);

  const getBarClass = (index: number) => {
    //? Determine the overall strength level
    const isStrong = strength === 3;
    const isMedium = strength === 2;
    const isWeak = strength === 1;

    //? Apply incremental color change based on the strength level
    if (index === 0) {
      //? For capital letter condition
      if (strength >= 1)
        return isWeak
          ? "bg-errorMain"
          : isWeak
            ? "bg-errorMain"
            : "bg-errorMain";
    }
    if (index === 1) {
      //? For length condition
      if (strength >= 2)
        return isStrong
          ? "bg-warningSecondary"
          : isMedium
            ? "bg-warningSecondary"
            : "bg-warningSecondary";
    }
    if (index === 2) {
      //? For number condition
      if (strength >= 3)
        return isStrong
          ? "bg-successMain"
          : isStrong
            ? "bg-warningSecondary"
            : "bg-errorSecondary";
    }

    return "bg-gray-300"; //? Default color for when no strength is met
  };

  return (
    <div className="w-full max-w-full">
      <div className="mb-4 flex w-full items-center justify-between gap-4">
        <div className={`h-0.5 w-full rounded ${getBarClass(0)}`} />
        <div className={`h-0.5 w-full rounded ${getBarClass(1)}`} />
        <div className={`h-0.5 w-full rounded ${getBarClass(2)}`} />
      </div>

      <ul className="mt-2 flex list-disc flex-col px-4">
        <li
          className={`text-sm ${
            /[A-Z]/.test(password) ? "text-successMain" : "text-textColor"
          }`}
        >
          {t("must_use_one_capital_letter")}
        </li>
        <li
          className={`text-sm ${
            password.length >= 8 ? "text-successMain" : "text-textColor"
          }`}
        >
          {t("must_be_8_character_minimum")}
        </li>
        <li
          className={`text-sm ${
            /\d/.test(password) ? "text-successMain" : "text-textColor"
          }`}
        >
          {t("must_use_one_number")}
        </li>
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicator;
